import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api, type RenderedDocument, type TopicDetail } from '../lib/api.ts'
import { brandToMergeFields, useBrandKit } from '../brand/BrandKitContext.tsx'
import { slugForDepartment } from '../lib/departments.ts'
import { recordToolUse } from '../lib/recents.ts'
import { RenderedDoc } from './RenderedDoc.tsx'

const titleCase = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : '')

/** A single artefact — policy, procedure or form — merged with the brand kit. */
export function DocumentPage() {
  const { topicId = '', artefactId = '' } = useParams()
  const { brand } = useBrandKit()
  const [doc, setDoc] = useState<RenderedDocument | null>(null)
  const [topic, setTopic] = useState<TopicDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setDoc(null)
    setTopic(null)
    setError(null)
    // Started alongside the render rather than after it: the topic supplies the
    // department for "recently used" and the titles for the related-document
    // links. Neither is worth failing the document over, so it stays on its own
    // error path — a topic lookup that hiccups costs the sidebar, not the page.
    const topicRequest = api.topic(topicId)
    api.render(artefactId, brandToMergeFields(brand))
      .then((rendered) => {
        setDoc(rendered)
        topicRequest
          .then((loaded) => {
            setTopic(loaded)
            recordToolUse({
              id: rendered.id,
              title: rendered.title,
              department: loaded.department,
              to: `/topics/${topicId}/${artefactId}`,
              isDraft: rendered.reviewStatus !== 'approved',
            })
          })
          .catch(() => {})
      })
      .catch(() => setError('Could not load this document. Please try again.'))
  }, [artefactId, brand, topicId])

  /**
   * The document's own cross-references, resolved to real links.
   *
   * An id we cannot resolve to a document in this topic is dropped rather than
   * linked: a reference pointing outside the pack would render as a dead link,
   * which is worse than the prose mention the source document already carries.
   */
  const related = useMemo(() => {
    if (!doc || !topic) return []
    const byId = new Map(topic.artefacts.map((a) => [a.id, a]))
    return (doc.relatedDocs ?? [])
      .filter((id) => id !== doc.id)
      .flatMap((id) => {
        const artefact = byId.get(id)
        return artefact ? [{ id, title: artefact.title, to: `/topics/${topicId}/${id}` }] : []
      })
  }, [doc, topic, topicId])

  if (error) return <main className="tool"><p className="notice notice--error">{error}</p></main>
  if (!doc) return <main className="tool"><p className="notice">Loading…</p></main>

  const badges = [titleCase(doc.type), topic?.quality_area].filter((b): b is string => Boolean(b))

  return (
    <RenderedDoc
      title={doc.title}
      markdown={doc.markdown}
      disclaimer={doc.disclaimer}
      reviewStatus={doc.reviewStatus}
      badges={badges}
      docControl={doc.docControl}
      related={related}
      // Built only once the topic can name the levels above this one; without
      // it the trail would be a chain of unlabelled links.
      crumbs={topic ? [
        { label: 'Departments', to: '/' },
        { label: topic.department, to: `/departments/${slugForDepartment(topic.department)}` },
        { label: topic.title, to: `/topics/${topicId}` },
        { label: doc.title },
      ] : undefined}
    />
  )
}
